import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useAskKiki } from './context/AskKikiContext';
import FloatingButton from './FloatingButton';
import ChatWindow from './ChatWindow';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';

const AskKikiWidget: React.FC = () => {
  const { isOpen, unreadCount, setIsOpen } = useAskKiki();
  const dragControls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const [constraints, setConstraints] = useState({ top: 0, left: 0, right: 0, bottom: 0 });
  const [chatOffset, setChatOffset] = useState({ left: -316, top: -536 });
  const [chatSize, setChatSize] = useState({ width: 380, height: 600 });

  const calculateConstraintsAndOffsets = () => {
    const margin = 16;
    const buttonSize = 64;
    // The fixed wrapper is bottom-6 right-6 -> 24px from edge
    const initialLeft = window.innerWidth - 24 - buttonSize; 
    const initialTop = window.innerHeight - 24 - buttonSize;

    if (!isOpen) {
      // Just button constraints
      setConstraints({
        left: margin - initialLeft,
        right: window.innerWidth - margin - buttonSize - initialLeft,
        top: margin - initialTop,
        bottom: window.innerHeight - margin - buttonSize - initialTop,
      });
      return;
    }

    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    
    const cw = Math.min(380, window.innerWidth - margin * 2);
    const ch = Math.min(600, window.innerHeight * 0.8);
    setChatSize({ width: cw, height: ch });

    // 1. Calculate preferred screen coordinates
    // Try to align bottom-right of chat to bottom-right of button
    let targetLeft = rect.right - cw;
    let targetTop = rect.bottom - ch;

    // Clamp horizontally to screen
    targetLeft = Math.max(margin, Math.min(targetLeft, window.innerWidth - margin - cw));
    
    // Clamp vertically to screen
    targetTop = Math.max(margin, Math.min(targetTop, window.innerHeight - margin - ch));

    // Convert screen coordinates to relative offsets
    const newOffsetLeft = targetLeft - rect.left;
    const newOffsetTop = targetTop - rect.top;

    setChatOffset({
      left: newOffsetLeft,
      top: newOffsetTop
    });

    // 2. Adjust constraints so the *Chat Window* cannot be dragged off screen
    setConstraints({
      left: margin - initialLeft - newOffsetLeft,
      right: window.innerWidth - margin - cw - initialLeft - newOffsetLeft,
      top: margin - initialTop - newOffsetTop,
      bottom: window.innerHeight - margin - ch - initialTop - newOffsetTop,
    });
  };

  useLayoutEffect(() => {
    calculateConstraintsAndOffsets();
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => calculateConstraintsAndOffsets();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  return (
    <motion.div 
      ref={containerRef}
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragConstraints={constraints}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={(e, info) => {
        setTimeout(() => setIsDragging(false), 150);
        if (isOpen) calculateConstraintsAndOffsets();
      }}
      data-no-print
      className="fixed bottom-6 right-6 z-50 w-16 h-16 askkiki-widget"
      style={{ touchAction: 'none' }}
    >
      <AnimatePresence>
        {isOpen && (
          <div 
            className="absolute shadow-2xl rounded-2xl"
            style={{ 
              left: chatOffset.left, 
              top: chatOffset.top,
              width: chatSize.width,
              height: chatSize.height 
            }}
          >
            <ChatWindow key="chat-window" dragControls={dragControls} />
          </div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {!isOpen && (
          <div key="floating-button" className="w-full h-full">
            <FloatingButton 
              onClick={() => {
                if (!isDragging) setIsOpen(true);
              }} 
              unreadCount={unreadCount} 
              dragControls={dragControls} 
            />
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AskKikiWidget;
